import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Slider } from './ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Sticker as StickerIcon, Plus, Trash2, Lock, Crown, Search, X, Eye, MousePointerClick, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { stickerUrl, MAX_STICKERS } from '../utils/stickers';
import { api } from '../utils/api';

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const useContainerWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => setW(entries[0].target.offsetWidth));
    ro.observe(ref.current);
    setW(ref.current.offsetWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

/* ─── Sticker layer ───
   Mount inside any `position: relative` profile container. Positions are
   stored in container-width units (x: 0-100% of width, y + size: % of width)
   so stickers scale proportionally between the live preview phone frame and
   the real public page — both use this exact same renderer, so placement is
   always WYSIWYG.

   Read-only mode (public page): fully pointer-events-none so links stay
   clickable. Editable mode (Settings live preview): each sticker becomes
   draggable + selectable, with a delete handle on the selected one. */
export const StickerLayer = ({ stickers, editable = false, selectedId = null, onSelect, onChange }) => {
  const [ref, w] = useContainerWidth();

  const startDrag = (e, id) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect && onSelect(id);
    const layer = ref.current;
    const move = (ev) => {
      if (!layer) return;
      const rect = layer.getBoundingClientRect();
      if (rect.width === 0) return;
      const maxY = Math.min(400, (rect.height / rect.width) * 100);
      const x = ((ev.clientX - rect.left) / rect.width) * 100;
      const y = ((ev.clientY - rect.top) / rect.width) * 100;
      onChange && onChange((prev) => (prev || []).map((s) => (
        s.id === id ? { ...s, x: clamp(x, 0, 100), y: clamp(y, 0, maxY) } : s
      )));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const removeSticker = (id) => {
    onChange && onChange((prev) => (prev || []).filter((s) => s.id !== id));
    if (onSelect && selectedId === id) onSelect(null);
  };

  if (!stickers || stickers.length === 0) return null;

  return (
    <div
      ref={ref}
      className="absolute inset-0 pointer-events-none z-30 overflow-visible"
      aria-hidden={!editable}
      data-testid="sticker-layer"
    >
      {w > 0 && stickers.map((s) => {
        const style = {
          left: `${s.x}%`,
          top: (s.y / 100) * w,
          width: (s.size / 100) * w,
          height: (s.size / 100) * w,
          transform: `translate(-50%, -50%) rotate(${s.rotation || 0}deg)`,
        };
        if (!editable) {
          return (
            <img key={s.id} src={stickerUrl(s.src)} alt="" draggable={false} loading="lazy"
              className="absolute select-none" style={style} />
          );
        }
        return (
          <div
            key={s.id}
            onPointerDown={(e) => startDrag(e, s.id)}
            className={`absolute cursor-move rounded-lg pointer-events-auto ${selectedId === s.id ? 'ring-2 ring-primary ring-offset-1' : ''}`}
            style={{ ...style, touchAction: 'none' }}
            data-testid={`sticker-item-${s.id}`}
          >
            <img src={stickerUrl(s.src)} alt="" draggable={false} className="w-full h-full pointer-events-none select-none" />
            {selectedId === s.id && (
              <button
                type="button"
                onPointerDown={(e) => e.stopPropagation()}
                onClick={() => removeSticker(s.id)}
                className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-destructive text-white flex items-center justify-center shadow"
                style={{ transform: `rotate(${-(s.rotation || 0)}deg)` }}
                data-testid="sticker-delete-handle"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ─── Sticker picker dialog — exclusive AI-generated packs ─── */
const StickerPicker = ({ open, onClose, onPick }) => {
  const [search, setSearch] = useState('');
  const [packs, setPacks] = useState(null); // null = loading
  const [activePack, setActivePack] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    if (!open || packs !== null) return;
    api.getStickerCatalog()
      .then((data) => {
        const p = data.packs || [];
        setPacks(p);
        if (p.length) setActivePack(p[0].id);
      })
      .catch(() => setLoadError(true));
  }, [open, packs]);

  const results = useMemo(() => {
    if (!packs) return [];
    const q = search.trim().toLowerCase();
    if (q) return packs.flatMap((p) => p.stickers).filter((s) => s.name.toLowerCase().includes(q));
    return packs.find((p) => p.id === activePack)?.stickers || [];
  }, [packs, search, activePack]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg p-0 gap-0" data-testid="sticker-picker-dialog">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <StickerIcon className="w-4 h-4 text-primary" /> Pick a sticker
          </DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stickers… (e.g. monkey, heart, disco)"
              className="pl-9"
              data-testid="sticker-search-input"
            />
          </div>
        </div>
        {!search.trim() && packs && packs.length > 0 && (
          <div className="flex gap-1.5 px-4 pb-2 overflow-x-auto scrollbar-hide">
            {packs.map((pack) => (
              <button
                key={pack.id}
                type="button"
                onClick={() => setActivePack(pack.id)}
                className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  activePack === pack.id ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`sticker-pack-${pack.id}`}
              >
                {pack.name}
              </button>
            ))}
          </div>
        )}
        <div className="h-[320px] overflow-y-auto px-4 pb-3" data-testid="sticker-grid">
          {loadError ? (
            <p className="text-sm text-muted-foreground text-center py-12">Could not load stickers — try again in a moment.</p>
          ) : packs === null ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : results.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              {search.trim() ? `No stickers found for "${search}"` : 'Sticker packs are on their way — check back shortly!'}
            </p>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {results.map((s) => (
                <button
                  key={s.path}
                  type="button"
                  title={s.name}
                  onClick={() => onPick(s)}
                  className="aspect-square rounded-lg p-1.5 hover:bg-muted transition-colors"
                  data-testid={`sticker-option-${s.slug}`}
                >
                  <img src={stickerUrl(s.path)} alt={s.name} loading="lazy" draggable={false} className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>
        <p className="text-[10px] text-muted-foreground text-center pb-3">
          Exclusive AI-generated LinkMonkey packs ✨
        </p>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Main editor (Settings → Appearance tab) ───
   Stickers are positioned by dragging them directly on the Live Preview —
   the exact same renderer as the public page, so placement is 1:1. This card
   only hosts the picker, the placed-sticker list and the selected sticker's
   size/rotation controls. */
export const StickerEditor = ({ stickers, onChange, isPro, selectedId, onSelect, onRequestPreview, previewOpen }) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const selected = (stickers || []).find((s) => s.id === selectedId) || null;

  const updateSelected = (patch) => {
    if (!selected) return;
    onChange((prev) => (prev || []).map((s) => (s.id === selected.id ? { ...s, ...patch } : s)));
  };

  const addSticker = (s) => {
    if ((stickers || []).length >= MAX_STICKERS) {
      toast.error(`You can place up to ${MAX_STICKERS} stickers`);
      return;
    }
    const id = `stk_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    onChange((prev) => [...(prev || []), {
      id, src: s.path,
      x: 25 + ((prev || []).length % 3) * 25,
      y: 15 + ((prev || []).length % 5) * 12,
      size: 14, rotation: 0,
    }]);
    onSelect && onSelect(id);
    setPickerOpen(false);
    if (!previewOpen && onRequestPreview) onRequestPreview();
    toast.success('Sticker added — drag it on the preview to position it');
  };

  const removeSticker = (id) => {
    onChange((prev) => (prev || []).filter((s) => s.id !== id));
    if (selectedId === id && onSelect) onSelect(null);
  };

  if (!isPro) {
    return (
      <div className="text-center py-8" data-testid="stickers-pro-lock">
        <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-950/50 flex items-center justify-center mx-auto mb-3">
          <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <p className="font-semibold mb-1">Stickers are a Pro feature</p>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
          Decorate your profile with exclusive AI-generated sticker packs — drag them anywhere, resize and rotate. Pure MySpace vibes.
        </p>
        <Link to="/pricing">
          <Button size="sm" className="gap-1.5" data-testid="stickers-upgrade-btn">
            <Crown className="w-3.5 h-3.5" /> Upgrade to Pro
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="sticker-editor">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">{(stickers || []).length}/{MAX_STICKERS} placed</p>
        <div className="flex gap-2">
          {!previewOpen && (
            <Button size="sm" variant="ghost" onClick={onRequestPreview} data-testid="stickers-open-preview-btn">
              <Eye className="w-3.5 h-3.5 mr-1" /> Open preview
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="add-sticker-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add sticker
          </Button>
        </div>
      </div>

      {(stickers || []).length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">
            No stickers yet — hit <span className="font-semibold">Add sticker</span> to start decorating.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
            <MousePointerClick className="w-3.5 h-3.5 shrink-0" />
            Drag stickers directly on the Live Preview to position them — it matches your public page exactly.
          </div>
          {/* Placed sticker strip — tap to select */}
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="placed-sticker-strip">
            {(stickers || []).map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => onSelect && onSelect(selectedId === s.id ? null : s.id)}
                className={`w-10 h-10 rounded-lg border p-1.5 transition-colors ${
                  selectedId === s.id ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                }`}
                data-testid={`placed-sticker-${s.id}`}
              >
                <img src={stickerUrl(s.src)} alt="" draggable={false} className="w-full h-full" />
              </button>
            ))}
          </div>
        </>
      )}

      {/* Selected sticker controls */}
      {selected && (
        <div className="p-3 rounded-xl border border-border bg-muted/30 space-y-3" data-testid="sticker-controls">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold flex items-center gap-1.5">
              <img src={stickerUrl(selected.src)} alt="" className="w-5 h-5" /> Selected sticker
            </span>
            <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive" onClick={() => removeSticker(selected.id)} data-testid="sticker-delete-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
            </Button>
          </div>
          <div>
            <div className="flex justify-between text-[11px] text-muted-foreground mb-1.5">
              <span>Size</span><span>{Math.round(selected.size)}%</span>
            </div>
            <Slider value={[selected.size]} min={5} max={60} step={1}
              onValueChange={([v]) => updateSelected({ size: v })} data-testid="sticker-size-slider" />
          </div>
          <div>
            <div className="flex justify-between text-[11px] text-muted-foreground mb-1.5">
              <span>Rotation</span><span>{Math.round(selected.rotation || 0)}°</span>
            </div>
            <Slider value={[selected.rotation || 0]} min={-180} max={180} step={1}
              onValueChange={([v]) => updateSelected({ rotation: v })} data-testid="sticker-rotation-slider" />
          </div>
        </div>
      )}

      <StickerPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={addSticker} />
    </div>
  );
};
